import { Component, type ComponentChildren } from 'preact';
import type { ErrorInfo } from 'preact';
import {
  reportError,
  initGlobalErrorHandler,
  getCorrelationId,
} from '../lib/client-log';

// Re-export the error-reporting helpers so consumers can import everything
// from a single module entry point.
export { reportError, initGlobalErrorHandler, getCorrelationId };

interface Props {
  children: ComponentChildren;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

/**
 * Preact error boundary — catches errors during rendering, in lifecycle
 * methods, and in constructors of the whole tree below it.
 *
 * Preact's `Component` supports `getDerivedStateFromError` and
 * `componentDidCatch` just like React's; the only difference is the
 * import source (`preact` vs `react`) and the `ErrorInfo` type path.
 *
 * NOTE: This does NOT catch errors in async code, event handlers, or
 * setTimeout callbacks. Those are caught by the global handlers
 * installed by `initGlobalErrorHandler()` (see client-log.ts).
 */
export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    reportError({
      type: 'react_error_boundary',
      message: error.message,
      stack: error.stack ?? '',
      componentStack: errorInfo.componentStack ?? '',
      url: typeof window !== 'undefined' ? window.location.href : '',
      timestamp: Date.now(),
      correlationId: getCorrelationId(),
    });
  }

  render() {
    if (this.state.hasError) {
      return (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: 'calc(100vh - 52px)',
            padding: '20px',
          }}
        >
          <div
            style={{
              maxWidth: '440px',
              width: '100%',
              textAlign: 'center',
              background: 'var(--surface)',
              border: '1px solid var(--border)',
              borderRadius: '12px',
              padding: '32px',
            }}
          >
            <h2
              style={{
                fontSize: '20px',
                fontWeight: 600,
                marginBottom: '8px',
                color: 'var(--danger)',
              }}
            >
              Something went wrong
            </h2>
            <p
              style={{
                color: 'var(--muted)',
                fontSize: '14px',
                lineHeight: 1.5,
                marginBottom: '20px',
                fontFamily: 'var(--mono)',
                wordBreak: 'break-word',
              }}
            >
              {this.state.error?.message || 'An unexpected error occurred.'}
            </p>
            <button
              className="btn"
              style={{ width: '100%' }}
              onClick={() => window.location.reload()}
            >
              Reload page
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
